import React, { useState } from "react";
import { Check } from "@phosphor-icons/react";
import { COLORS as VOLANTINO_COLORS } from "@/components/StaticTemplatePreview";

/**
 * BrandColorPicker — 8 palette unificate col Volantino/Brochure/Story.
 *
 * Iter 287: il picker ora mostra `swatch` (il colore "brand" riconoscibile
 * della palette), NON l'accent scuro. Prima succedeva che la palette "Notte"
 * appariva come quadratino oro/senape (perché accent=#C8B886), e "Nebbia"
 * come verde scuro. Confusione totale per l'utente.
 *
 * Manteniamo `accent` come valore salvato in user.brand_color per non
 * rompere l'integrazione col Volantino (che fa match sul HEX accent).
 *
 * Sotto ogni swatch ora compare anche il NOME della palette ("Notte",
 * "Senape", ecc.) così l'utente sa esattamente cosa sta scegliendo.
 */
// iter v28 r218 — User M1138: dedupa per accent (gli alias retro-compat
// `verde_brera`, `bosco`, `senape`, ecc. puntano allo stesso accent dei
// colori canonici → causavano 25+ pallini invece di 15).
const _seen = new Set();
const PRESETS = Object.entries(VOLANTINO_COLORS).reduce((acc, [key, c]) => {
  const k = (c.accent || "").toLowerCase();
  if (_seen.has(k)) return acc;
  _seen.add(k);
  acc.push({
    key,
    name: c.name,
    swatch: c.swatch,
    value: c.accent,
    isDark: !!c.isDark,
  });
  return acc;
}, []);

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

export default function BrandColorPicker({ value, onChange, variant = "grid" }) {
  const current = (value || "").toLowerCase();
  const [custom, setCustom] = useState(value && !PRESETS.some((p) => p.value.toLowerCase() === current) ? value : "");

  const onPreset = (hex) => {
    onChange(hex);
    setCustom("");
  };

  const onCustomChange = (e) => {
    const raw = e.target.value;
    setCustom(raw);
    if (HEX_RE.test(raw)) {
      const normalized = raw.startsWith("#") ? raw : `#${raw}`;
      onChange(normalized.toUpperCase());
    }
  };

  // iter v28 r44 — Variante "circle": 8 cerchietti compatti in fila + HEX
  // inline. Stile minimal, niente etichette sotto. Ideale per panel laterali
  // tipo "Stile della pagina online" dove serve risparmiare spazio verticale.
  if (variant === "circle") {
    return (
      <div data-testid="brand-color-picker-circle">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {PRESETS.map(({ name, swatch, value: hex }) => {
            const active = current === hex.toLowerCase();
            // iter v28 r237 — User M1153: il pallino BIANCO su sfondo
            // bianco era invisibile → l'utente ne contava 14 invece di 15.
            // Forziamo un border esterno più scuro per i due estremi:
            // bianco (sfondo card lo fa sparire) e nero (ring-offset chiaro
            // lo rende confondibile con scuri scuri).
            const isWhite = swatch.toLowerCase() === "#ffffff";
            const ringIdle = isWhite ? "ring-[#9A968B]" : "ring-[#E5E7E0]";
            return (
              <button
                key={hex}
                type="button"
                onClick={() => onPreset(hex)}
                title={name}
                aria-label={name}
                className={`relative rounded-full transition-all flex-shrink-0 p-0 m-0 appearance-none ${
                  active ? "ring-2 ring-[#1C1C1A] ring-offset-2 scale-110" : `ring-1 ${ringIdle} hover:ring-[#7C8B6E] hover:scale-105`
                }`}
                style={{
                  width: 32, height: 32,
                  minWidth: 32, minHeight: 32,
                  maxWidth: 32, maxHeight: 32,
                  boxSizing: "border-box",
                  lineHeight: 0,
                  backgroundColor: swatch,
                }}
                data-testid={`color-preset-${hex.replace("#", "")}`}
              />
            );
          })}
          {/* iter v28 r75 — Rimosso preview cerchietto + input HEX (utente
              li confondeva con un 9° colore). Per HEX custom usare variant="grid". */}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="brand-color-picker">
      {/* iter v32 r37r · User M1448 — Swatches più compatti su mobile (h-10
          invece di h-14) coerenti con i picker dell'editor social. Su
          desktop restano leggermente più grandi (sm:h-12) per leggibilità. */}
      <div className="grid grid-cols-5 sm:grid-cols-8 gap-2 sm:gap-2.5">
        {PRESETS.map(({ name, swatch, value: hex, isDark }) => {
          const active = current === hex.toLowerCase();
          const isWhite = swatch.toLowerCase() === "#ffffff";
          const ringIdle = isWhite ? "ring-[#9A968B]" : "ring-[#E5E7E0]";
          return (
            <button
              key={hex}
              type="button"
              onClick={() => onPreset(hex)}
              title={name}
              aria-label={name}
              className="group flex flex-col items-center gap-1 min-w-0"
              data-testid={`color-preset-${hex.replace("#", "")}`}
            >
              <span
                className={`relative h-9 sm:h-11 w-full rounded-md transition-all flex items-center justify-center ${
                  active ? "ring-2 ring-[#1C1C1A] ring-offset-1 scale-[1.04]" : `ring-1 ${ringIdle} group-hover:ring-[#7C8B6E]`
                }`}
                style={{ backgroundColor: swatch }}
              >
                {active && (
                  <Check size={14} weight="bold" className={isWhite ? "text-[#1C1C1A]" : (isDark ? "text-white drop-shadow" : "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]")} />
                )}
              </span>
              <span className={`text-[9px] sm:text-[10px] uppercase tracking-[0.08em] sm:tracking-[0.14em] leading-tight text-center truncate w-full ${
                active ? "text-[#1C1C1A] font-semibold" : "text-[#6B6A65]"
              }`}>
                {name}
              </span>
            </button>
          );
        })}
      </div>

      {/* iter v32 r37u · User M1452 — HEX row wrappable e label compatta
          su mobile (HEX invece di "O codice HEX") per evitare overflow
          del bottone Reset sui device più stretti. */}
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <label className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65] flex-shrink-0">
          <span className="hidden sm:inline">O codice </span>HEX
        </label>
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <span
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-md border border-[#E5E7E0] flex-shrink-0"
            style={{ backgroundColor: HEX_RE.test(custom) ? (custom.startsWith("#") ? custom : `#${custom}`) : (current || "#F1F4ED") }}
            aria-hidden
          />
          <input
            data-vista
            type="text"
            value={custom}
            onChange={onCustomChange}
            placeholder="#7C8B6E"
            maxLength={7}
            className="flex-1 min-w-0 font-mono uppercase text-[13px]"
            data-testid="brand-color-hex-input"
          />
        </div>
        {value && (
          <button
            type="button"
            onClick={() => { onChange(""); setCustom(""); }}
            className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] font-semibold text-[#9A968B] hover:text-[#1C1C1A] flex-shrink-0 px-2 py-1 rounded-full border border-[#E5E7E0] hover:border-[#1C1C1A]"
            data-testid="brand-color-clear"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
